import React, { useContext, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image, Card } from '@rneui/themed';
import { SvgUri } from 'react-native-svg';
import FontAwesome6 from '@expo/vector-icons/FontAwesome6';
import PropTypes from 'prop-types';
import { Broadcast } from './Broadcast';
import { Streaming } from './Streaming';
import { H1, H3, H4, H6 } from '../../text';
import LayoutContext from '../../../contexts/Layout';

const HowToWatch = ({ contentItems, description, nodeTypeAlias = 'HowToWatch', title, width }) => {
    const { layout } = useContext(LayoutContext);
    const items = Object.values(contentItems);
    const [active, setActive] = useState(items[0]);

    const renderOptions = () => {
        return (
            <View style={{ flexDirection: 'row' }}>
                {items.map((option, index) => {
                    const optionId = option?.id;
                    const optionTitle = option?.title;
                    const optionImgSrc = option?.image?.cdnImagePath;
                    const extension = optionImgSrc.substring(optionImgSrc?.lastIndexOf('.') + 1, optionImgSrc.length);
                    const isActive = active?.id === optionId;
                    const horizontalMargin = index % 2 !== 0 ? layout.styleGuide.indent : 0;
                    const backgroundColor = isActive ? '#153d6e' : '#8897a2';
                    const indent = value => (Platform.OS.equals('web') ? `${value}pt` : value);
                    const borderStyle = {
                        backgroundColor: backgroundColor,
                        borderColor: 'white',
                        borderStyle: 'solid',
                        borderRightWidth: index < items.length - 1 ? indent(layout.styleGuide.indent) : 0
                    };

                    return (
                        <Pressable key={optionId} onPress={() => setActive(option)} style={{ flex: 1, ...borderStyle }}>
                            <Card
                                containerStyle={[
                                    Styles.watchOption,
                                    { backgroundColor: backgroundColor, horizontalMargin: horizontalMargin },
                                    !Platform.OS.equals('web') && { ...borderStyle }
                                ]}
                                dataSet={
                                    horizontalMargin > 0
                                        ? { spacerLeft: `StyleGuide.Indent(${layout.styleGuide.indent})`, spacerRight: `StyleGuide.Indent(${layout.styleGuide.indent})` }
                                        : undefined
                                }
                            >
                                {extension.equals('svg') && !Platform.OS.equals('web') && (
                                    <View style={{ backgroundColor: isActive ? '#153d6e' : '#8897a2', alignItems: 'center' }}>
                                        <SvgUri height={105} width={'87%'} uri={`https:${optionImgSrc}`} />
                                    </View>
                                )}
                                {
                                    /* 
										   This done to work around react-native-svg simple 
										   implementation of SVG on web platform 
										*/
                                    extension?.equals('svg') && Platform.OS.equals('web') && (
                                        <View
                                            style={{ alignItems: 'center', paddingHorizontal: layout.styleGuide.spacer4, paddingVertical: layout.styleGuide.spacer4 }}
                                            dataSet={{
                                                spacerBottom: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})`,
                                                spacerTop: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})`
                                            }}
                                        >
                                            <img src={`https:${optionImgSrc}`} style={StyleSheet.flatten(Styles.webSVGIcon, { width: '90%' })} />
                                        </View>
                                    )
                                }
                                {!extension?.equals('svg') && (
                                    <View
                                        style={{ alignItems: 'center', paddingVertical: layout.styleGuide.spacer4 }}
                                        dataSet={{
                                            spacerBottom: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})`,
                                            spacerTop: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})`
                                        }}
                                    >
                                        <Image source={{ uri: `https:${optionImgSrc}` }} containerStyle={Styles.imageIcon} />
                                    </View>
                                )}
                                <H4
                                    style={{
                                        paddingBottom: Platform.OS.equals('web') ? 0 : layout.styleGuide.spacer4,
                                        marginBottom: layout.styleGuide.spacer4,
                                        textAlign: 'center',
                                        color: isActive ? '#fff' : '#162b4a',
                                        backgroundColor: isActive ? '#153d6e' : '#8897a2'
                                    }}
                                    text={optionTitle}
                                    dataSet={{ spacerBottom: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})` }}
                                />
                            </Card>
                        </Pressable>
                    );
                })}
            </View>
        );
    };

    return (
        <View dataSet={{ umbracoComponentName: nodeTypeAlias }}>
            <View>
                <View style={Styles.textContainer}>
                    <H1 text={title} />
                </View>
                <View style={Styles.textContainer}>
                    <H6 numberOfLines={1} text={description} />
                </View>
                {renderOptions()}
                <View
                    style={{ alignItems: 'center', marginBottom: layout.styleGuide.spacer3, marginTop: layout.styleGuide.spacer4 }}
                    dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`, spacerTop: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})` }}
                >
                    <FontAwesome6 name='chevron-down' size={20} color='#8897a2' />
                </View>
                <View
                    style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: layout.styleGuide.spacer2 }}
                    dataSet={{ spacerBottom: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                >
                    <H3 text={active.title} />
                </View>
                {active?.nodeTypeAlias?.equals('reactBroadcastItem') && <Broadcast items={active.contentItems} nodeTypeAlias={active.nodeTypeAlias} width={width} />}
                {active?.nodeTypeAlias?.equals('reactStreamingItem') && <Streaming items={active.contentItems} nodeTypeAlias={active.nodeTypeAlias} width={width} />}
            </View>
        </View>
    );
};

const Styles = StyleSheet.create({
    imageIcon: {
        height: 75,
        width: '100%'
    },
    textContainer: {
        flexDirection: 'row',
        justifyContent: 'center'
    },
    watchOption: {
        borderBottomWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderTopWidth: 0,
        justifyContent: 'center',
        marginVertical: 0,
        marginHorizontal: 0,
        padding: 0
    },
    webSVGIcon: {
        color: '#fff',
        height: '100%',
        width: '100%'
    }
});

HowToWatch.propTypes = {
    activeTitle: PropTypes.string,
    contentItems: PropTypes.object,
    howToWatchOptions: PropTypes.array,
    howToWatchTitle: PropTypes.string,
    description: PropTypes.string
};

export default HowToWatch;
